/**
 * IntelStreamLayout — tab shell that groups Intel Stream + Conviction Board +
 * Event Calendar + Video Pipeline under one sidebar entry.
 *
 * These routes don't share a common URL prefix (/intel/stream,
 * /intel/convictions, /intel/events, /intel/videos), so this layout accepts
 * its child page as a `children` prop rather than using <Outlet/>. Each
 * route wraps itself: <Route element={<IntelStreamLayout><Page/></IntelStreamLayout>}/>.
 * Preserves existing URLs + detail routes.
 */

import { NavLink } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const BASE_TABS = [
  { to: "/intel/stream", label: "Stream", permission: "intel.configure" },
  { to: "/intel/convictions", label: "Conviction", permission: "intel.view" },
  { to: "/intel/events", label: "Events", permission: "intel.view" },
  { to: "/intel/videos", label: "Videos", permission: "intel.configure" },
];

export default function IntelStreamLayout({ children }) {
  const { hasPermission } = useAuth();
  const tabs = BASE_TABS.filter((t) => !t.permission || hasPermission(t.permission));

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-1 px-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-x-auto flex-shrink-0">
        <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mr-3 whitespace-nowrap">
          Intel Stream
        </span>
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end
            className={({ isActive }) =>
              `px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                isActive
                  ? "border-blue-600 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              }`
            }
          >
            {t.label}
          </NavLink>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto min-h-0">{children}</div>
    </div>
  );
}
